import type { ApplicationStage, OutList, RankingRow } from '@nest-recruit/shared';

// ④ Review's Rejected lane (#32). RNK-01's rows leave out everyone rejected (RANKING_EXCLUDED_STAGES),
// so the lane can't be drawn from them: its cards and its count both come from the job's Out list
// (§L33, outcome rejected) — the same people the stage bar's "Out" counts. A card just rejected
// shows at once from the ranking's optimistic copy, before the Out list has refetched (§5a rule 1).

/** Where a rejection counts as made in review from (F23.18). */
const REVIEW_STAGES: ReadonlySet<ApplicationStage> = new Set<ApplicationStage>(['new', 'screening']);

export interface RejectedCard {
  readonly applicationId: string;
  readonly name: string;
  readonly reasonLabel: string | null;
  /** Rejected on this screen, not yet confirmed by the server. */
  readonly isSaving: boolean;
  /** Rejected during review (F23.18): may be brought back into a lane while the job is open. */
  readonly isFromReview: boolean;
}

export interface RejectedLane {
  readonly cards: readonly RejectedCard[];
  readonly count: number;
}

export function rejectedLaneOf(rows: readonly RankingRow[], out: OutList | undefined): RejectedLane {
  const confirmed = out?.rows ?? [];
  const known = new Set(confirmed.map((row) => row.applicationId));
  const justRejected: RejectedCard[] = rows
    .filter((row) => row.stage === 'rejected' && !known.has(row.applicationId))
    .map((row) => ({ applicationId: row.applicationId, name: row.name, reasonLabel: null, isSaving: true, isFromReview: true }));
  const cards = [
    ...justRejected,
    ...confirmed.map((row) => ({
      applicationId: row.applicationId,
      name: row.candidate.fullName,
      reasonLabel: row.reasonLabel,
      isSaving: false,
      isFromReview: row.leftFrom === null || REVIEW_STAGES.has(row.leftFrom),
    })),
  ];
  return { cards, count: (out?.total ?? 0) + justRejected.length };
}
